import { useEffect, useRef, type PropsWithChildren } from 'react'
import { toast } from 'sonner'
import {
  AUTH_PROFILE_META_STORAGE_KEY,
  AUTH_PROFILE_STORAGE_KEY,
  type Profile,
  useAuthStore,
} from '@/stores/auth-store'
import { apiFetch, isAccountBlockedError } from '@/lib/api-client'

// Public routes that don't require authentication
const PUBLIC_ROUTES = [
  '/sign-in',
  '/sign-up',
  '/forgot-password',
  '/reset-password',
]

function isPublicRoute(): boolean {
  if (typeof window === 'undefined') return false
  const pathname = window.location.pathname
  return PUBLIC_ROUTES.some((route) => pathname.startsWith(route))
}

export function AuthProvider({ children }: PropsWithChildren) {
  const status = useAuthStore((state) => state.status)
  const profileStatus = useAuthStore((state) => state.profileStatus)
  const setAuth = useAuthStore((state) => state.setAuth)
  const setProfile = useAuthStore((state) => state.setProfile)
  const setProfileStatus = useAuthStore((state) => state.setProfileStatus)
  const setStatus = useAuthStore((state) => state.setStatus)
  const loadingToastRef = useRef<string | number | null>(null)
  const didHydrateRef = useRef(false)

  useEffect(() => {
    const isLoading = status === 'authenticated' && profileStatus === 'loading'
    if (isLoading && !loadingToastRef.current) {
      loadingToastRef.current = toast.loading('Sincronizando sesion...')
    }

    if (!isLoading && loadingToastRef.current) {
      toast.dismiss(loadingToastRef.current)
      loadingToastRef.current = null
    }
  }, [profileStatus, status])

  useEffect(() => {
    const hydrateProfileCache = () => {
      const storedProfile = localStorage.getItem(AUTH_PROFILE_STORAGE_KEY)
      if (!storedProfile) return
      try {
        const cached = JSON.parse(storedProfile) as Profile
        if (cached?.id) {
          let updatedAt: number | undefined
          const meta = localStorage.getItem(AUTH_PROFILE_META_STORAGE_KEY)
          if (meta) {
            try {
              const parsedMeta = JSON.parse(meta) as { updatedAt?: number }
              if (parsedMeta?.updatedAt) {
                updatedAt = parsedMeta.updatedAt
              }
            } catch (metaError) {
              // eslint-disable-next-line no-console
              console.warn('Failed to parse stored profile metadata', metaError)
            }
          }

          setProfile(cached, { updatedAt, persist: false })
          setProfileStatus('ready')
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Failed to parse stored profile', error)
        setProfile(null)
      }
    }

    const loadProfile = async () => {
      setProfileStatus('loading')
      try {
        const me = await apiFetch<{ success: boolean; data: Profile }>(
          '/api/auth/me'
        )
        if (me.success) {
          setProfile(me.data)
          setProfileStatus('ready')
        } else {
          setProfile(null)
          setProfileStatus('error')
        }
      } catch (error) {
        if (isAccountBlockedError(error)) {
          toast.error(error.message)
          setProfile(null)
          setProfileStatus('idle')
          return
        }
        if (error && typeof error === 'object' && 'status' in error) {
          const statusValue = Number((error as { status?: unknown }).status)
          if (statusValue === 401) {
            setProfile(null)
            setProfileStatus('idle')
            return
          }
        }
        // eslint-disable-next-line no-console
        console.warn('Failed to load profile', error)
        setProfile(null)
        setProfileStatus('error')
      }
    }

    const hydrateSession = async () => {
      if (typeof window === 'undefined') return

      // Skip session hydration on public routes
      if (isPublicRoute()) {
        setAuth(null)
        setStatus('unauthenticated')
        setProfile(null)
        return
      }

      hydrateProfileCache()
      try {
        await loadProfile()
      } finally {
        if (useAuthStore.getState().profile) {
          setAuth({ source: 'cookie' })
        } else {
          setAuth(null)
          setStatus('unauthenticated')
        }
      }
    }

    if (!didHydrateRef.current) {
      didHydrateRef.current = true
      void hydrateSession()
    }
  }, [setAuth, setProfile, setProfileStatus, setStatus])

  return <>{children}</>
}
